import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import { ROLES, ROLE_LABELS, ROLE_COLORS, ROLE_BG, setUserRole } from '../roles';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';

const ROLE_DESCS = {
  admin:     'Full access to everything',
  data:      'Pipeline, editor, quality tools',
  support:   'Help tickets, user profiles',
  marketing: 'Growth, campaigns, A/B tests',
};

const PENDING_KEY = 'sanoire_pending_invites';
export function getPendingInvites() {
  try { return JSON.parse(localStorage.getItem(PENDING_KEY) || '[]'); } catch (_e) { return []; }
}
export function savePendingInvites(arr) {
  try { localStorage.setItem(PENDING_KEY, JSON.stringify(arr)); } catch (_e) {}
}
export function removePendingInvite(email) {
  savePendingInvites(getPendingInvites().filter(i => i.email !== email));
}

export default function InviteModal({ onClose, onSuccess }) {
  const [email,   setEmail]   = useState('');
  const [role,    setRole]    = useState('support');
  const [note,    setNote]    = useState('');
  const [sending, setSending] = useState(false);
  const [error,   setError]   = useState('');

  async function handleSend() {
    const trimmed = email.trim().toLowerCase();
    // Email validation: check for @ symbol, basic format, and TLD
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!trimmed || !emailRegex.test(trimmed)) { setError('Enter a valid email address.'); return; }
    setSending(true); setError('');
    try {
      // Use OTP magic link — works without service role key, creates user if needed
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: trimmed,
        options: { shouldCreateUser: true },
      });
      if (otpError) throw otpError;

      // Store role assignment locally
      setUserRole(trimmed, role);
      const pending = getPendingInvites();
      const entry = { email: trimmed, role, note: note.trim(), sentAt: new Date().toISOString() };
      const idx = pending.findIndex(i => i.email === trimmed);
      if (idx >= 0) pending[idx] = entry; else pending.unshift(entry);
      savePendingInvites(pending);
      onSuccess?.();
      onClose();
    } catch (e) {
      setError(e.message || 'Failed to send invite.');
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 9500, background: 'rgba(10,22,32,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: F }}>
      <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 460,
        margin: '0 16px', border: '0.5px solid #d8eaf4', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>

        {/* Header */}
        <div style={{ padding: '20px 22px 16px', borderBottom: '.5px solid #edf6fb',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '.88rem', fontWeight: 600, color: '#1a3040', marginBottom: 3 }}>Invite a team member</div>
            <div style={{ fontSize: '.72rem', color: '#9eb8c8' }}>They'll receive a magic link — no password needed.</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer',
            color: '#9eb8c8', fontSize: '1.1rem', lineHeight: 1, padding: '0 2px', marginTop: -2 }}>×</button>
        </div>

        {/* Body */}
        <div style={{ padding: '18px 22px' }}>

          {/* Email */}
          <div style={{ fontSize: '.62rem', fontWeight: 600, color: '#9eb8c8',
            textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 5 }}>Email address</div>
          <input
            type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="name@company.com" autoFocus
            onKeyDown={e => e.key === 'Enter' && handleSend()}
            style={{ width: '100%', height: 36, border: error && !email ? '1px solid #e05c5c' : '0.5px solid #c8dce8',
              borderRadius: 8, padding: '0 10px', fontSize: '.8rem', color: '#1a3040',
              fontFamily: F, outline: 'none', marginBottom: 16, boxSizing: 'border-box' }}
          />

          {/* Role picker */}
          <div style={{ fontSize: '.62rem', fontWeight: 600, color: '#9eb8c8',
            textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 7 }}>Role</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7, marginBottom: 16 }}>
            {ROLES.map((r, i) => (
              <div key={r}
                onClick={() => setRole(r)}
                style={{
                  gridColumn: i === ROLES.length - 1 && ROLES.length % 2 !== 0 ? 'span 2' : undefined,
                  border: role === r ? '1.5px solid ' + ROLE_COLORS[r] : '1.5px solid #e4edf3',
                  borderRadius: 8, padding: '10px 12px', cursor: 'pointer',
                  background: role === r ? ROLE_BG[r] : '#fafcfd',
                  transition: 'border-color .1s, background .1s',
                }}
              >
                <div style={{ fontSize: '.74rem', fontWeight: 600, color: ROLE_COLORS[r], marginBottom: 2 }}>
                  {ROLE_LABELS[r]}
                </div>
                <div style={{ fontSize: '.67rem', color: '#9eb8c8', lineHeight: 1.4 }}>
                  {ROLE_DESCS[r]}
                </div>
              </div>
            ))}
          </div>

          {/* Note */}
          <div style={{ fontSize: '.62rem', fontWeight: 600, color: '#9eb8c8',
            textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 5 }}>
            Personal note
            <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0,
              color: '#c8dce8', marginLeft: 4 }}>(for your records)</span>
          </div>
          <textarea
            value={note} onChange={e => setNote(e.target.value)}
            placeholder="Add a note about this team member…"
            rows={2}
            style={{ width: '100%', border: '0.5px solid #c8dce8', borderRadius: 8,
              padding: '8px 10px', fontSize: '.75rem', color: '#1a3040',
              fontFamily: F, outline: 'none', resize: 'none', boxSizing: 'border-box', lineHeight: 1.5 }}
          />
          {error && <div style={{ marginTop: 6, fontSize: '.68rem', color: '#c0392b' }}>{error}</div>}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 22px 18px', borderTop: '.5px solid #edf6fb',
          display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '.63rem', color: '#c8dce8', lineHeight: 1.4 }}>
            Magic link sent via Resend
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose}
              style={{ height: 32, padding: '0 14px', borderRadius: 8, fontSize: '.76rem', fontWeight: 500,
                cursor: 'pointer', border: '0.5px solid #d8eaf4', background: '#fff', color: '#314F5C', fontFamily: F }}>
              Cancel
            </button>
            <button onClick={handleSend} disabled={sending}
              style={{ height: 32, padding: '0 16px', borderRadius: 8, fontSize: '.76rem', fontWeight: 600,
                cursor: sending ? 'default' : 'pointer', border: 'none',
                background: sending ? '#9eb8c8' : '#314F5C', color: '#e0f5fc',
                fontFamily: F, opacity: sending ? 0.75 : 1, transition: 'opacity .15s' }}>
              {sending ? 'Sending…' : 'Send invite →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
